import { useState, useEffect, useCallback, useRef } from 'react';
import { normalizeStatus } from '../utils/webcheckinStatus';
import { fetchBusiness4FlyoCheckIns } from '../services/api';

/**
 * Get today's date in YYYY-MM-DD format
 * @returns {string} Today's date string
 */
const getTodayDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Custom hook for managing Business4Flyo web check-ins
 * @returns {Object} Object containing check-in data and methods
 */
const useBusiness4FlyoCheckIns = () => {
  const [checkIns, setCheckIns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({
    itemsPerPage: 25,
    pageNumber: 1, // 1-based for API
    totalItems: 0,
    totalPages: 0
  });

  // Filter states for Business4Flyo API - default startDepartureDate to today
  const [filters, setFilters] = useState({
    keyword: '', // Search by agent name or email
    agentId: '',
    checkinStatuses: '', // Comma-separated statuses
    startDepartureDate: getTodayDateString(), // Default to today's date
    endDepartureDate: '',
    startScheduledTime: '',
    endScheduledTime: ''
  });

  // Keep a ref of filters to avoid recreating callbacks and retriggering effects
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Track requests in refs so loadCheckIns stays stable across renders
  const hasInitiallyLoadedRef = useRef(false);
  const lastLoadParamsRef = useRef(null);
  const loadingRef = useRef(false);

  // Fetch all check-ins with filters
  const loadCheckIns = useCallback(async (
    page = 1,
    itemsPerPage = 25,
    customFilters = {},
    options = {}
  ) => {
    if (loadingRef.current) {
      console.log('Already loading Business4Flyo check-ins, skipping duplicate call');
      return;
    }

    setLoading(true);
    loadingRef.current = true;
    setError(null);

    try {
      // Ensure page is at least 1 (1-based pagination)
      const pageNumber = page < 1 ? 1 : page;
      
      const params = {
        page: pageNumber,
        limit: itemsPerPage,
        // Use the most recent filters from the ref to keep this callback stable
        ...filtersRef.current,
        ...customFilters // Allow overriding filters
      };

      // Map filter names to API parameter names
      const apiParams = {
        page: params.page,
        limit: params.limit
      };

      // Map keyword
      if (params.keyword) {
        apiParams.keyword = params.keyword;
      }

      // Map agentId
      if (params.agentId) {
        apiParams.agentId = params.agentId;
      }

      // Map checkinStatuses
      if (params.checkinStatuses) {
        apiParams.checkinStatuses = params.checkinStatuses;
      }

      // Map departure date filters
      if (params.startDepartureDate) {
        apiParams.startDepartureDate = params.startDepartureDate;
      }
      if (params.endDepartureDate) {
        apiParams.endDepartureDate = params.endDepartureDate;
      }

      // Map scheduled time filters
      if (params.startScheduledTime) {
        apiParams.startScheduledTime = params.startScheduledTime;
      }
      if (params.endScheduledTime) {
        apiParams.endScheduledTime = params.endScheduledTime;
      }

      // Check if we're making the same request as before
      const currentParams = JSON.stringify(apiParams);
      if (!options.force && hasInitiallyLoadedRef.current && lastLoadParamsRef.current === currentParams) {
        console.log('Skipping duplicate API call for Business4Flyo check-ins');
        setLoading(false);
        loadingRef.current = false;
        return;
      }

      console.log('Loading Business4Flyo check-ins with params:', apiParams);

      const response = await fetchBusiness4FlyoCheckIns(apiParams);

      // Cache the request parameters
      lastLoadParamsRef.current = currentParams;
      hasInitiallyLoadedRef.current = true;
      setCheckIns(response.data);

      // Update pagination info from API response
      setPagination(prev => ({
        ...prev,
        pageNumber: response.pagination.page || 1,
        itemsPerPage: response.pagination.limit || 25,
        totalItems: response.pagination.total || 0,
        totalPages: response.pagination.totalPages || 0
      }));
    } catch (err) {
      setError(err.message || 'Failed to load Business4Flyo check-ins');
      console.error('Error loading Business4Flyo check-ins:', err);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  // Load check-ins on mount
  useEffect(() => {
    if (!hasInitiallyLoadedRef.current) {
      console.log('Loading Business4Flyo check-ins for the first time');
      loadCheckIns(1, 25);
    }
  }, [loadCheckIns]);

  // Change page
  const changePage = useCallback((newPage) => {
    loadCheckIns(newPage, pagination.itemsPerPage);
  }, [loadCheckIns, pagination.itemsPerPage]);

  // Update filters without triggering API call
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Search with current filters
  const searchWithFilters = useCallback(() => {
    loadCheckIns(1, pagination.itemsPerPage, filters, { force: true });
  }, [loadCheckIns, pagination.itemsPerPage, filters]);

  // Clear all filters (but keep default startDepartureDate as today)
  const clearFilters = useCallback(() => {
    const clearedFilters = {
      keyword: '',
      agentId: '',
      checkinStatuses: '',
      startDepartureDate: getTodayDateString(), // Reset to today's date
      endDepartureDate: '',
      startScheduledTime: '',
      endScheduledTime: ''
    };
    setFilters(clearedFilters);
    loadCheckIns(1, pagination.itemsPerPage, clearedFilters, { force: true });
  }, [loadCheckIns, pagination.itemsPerPage]);

  // Filter check-ins by status (client-side filtering for status cards: 'all' or a status code)
  const filteredCheckIns = statusFilter === 'all'
    ? checkIns
    : checkIns.filter(ci => normalizeStatus(ci.status) === statusFilter);

  return {
    checkIns: filteredCheckIns,
    allCheckIns: checkIns,
    loading,
    error,
    statusFilter,
    setStatusFilter,
    pagination,
    changePage,
    loadCheckIns,
    // Filter functionality
    filters,
    updateFilters,
    searchWithFilters,
    clearFilters
  };
};

export default useBusiness4FlyoCheckIns;
